import React, { useEffect, useRef } from 'react';
import { isEmpty } from 'lodash';
import { useLocation } from 'react-router-dom';
import EmbeddedWebpage from '../../common/embeddedWebpage';
import InfoBox from '../../common/infoBox';
import { withTranslation } from '../../common/translation';
import { LOCAL_ACCESS_TOKEN } from '../../../constant/text';
import { TRIAGE_PAGE } from '../../../constant/route';
import { getAccountId, getAssignedProduct } from '../../util';
import { getFromLocal } from '../../../api/localStorageUtil';
import { getApiBaseUrl, isLoggedIn } from '../../../api/util';

const getTriageUrlForLanguage = (languageCode: string): string => process.env[`REACT_APP_TRIAGE_URL_${languageCode.toUpperCase()}`] || process.env.REACT_APP_TRIAGE_URL_EN || 'No Triage URL Configured';

/**
 * Persistent, keep-alive triage iframe (BAS-13617).
 *
 * Rendered OUTSIDE the route <Switch> so it survives tab navigation: instead of
 * unmounting when the user leaves /triage, it is hidden with `display: none` and
 * shown again on return — keeping the embedded Aweria session (and the patient
 * visit the user is entering) alive.
 *
 * The iframe is remounted ONLY when the embedded session genuinely changes:
 *  - language switch  -> triageUrl changes
 *  - account switch   -> accountId / assigned product changes
 * A routine access-token refresh keeps the same account/product/language, so the
 * composite key is unchanged and the captured token is reused — no reload, no
 * lost visit (see requirement 4 in the ticket).
 */
const KeepAliveTriage = ({ t, i18n }) => {
    const location = useLocation();
    const isTriage = location.pathname === TRIAGE_PAGE;

    // Last-known-good session identity. Held in refs (not state) so a transient
    // empty product during a token refresh does not unmount the live iframe.
    const embedKeyRef = useRef<string>('');
    const urlRef = useRef<string>('');
    const tokenRef = useRef<string>('');
    const productRef = useRef<any>(null);

    const loggedIn = isLoggedIn();

    // On logout, reset so a later login must re-open /triage and recaptures a
    // fresh token instead of reusing the previous session's values.
    useEffect(() => {
        if (!loggedIn) {
            embedKeyRef.current = '';
            urlRef.current = '';
            tokenRef.current = '';
            productRef.current = null;
        }
    }, [loggedIn]);

    const product = getAssignedProduct();
    const noProduct = !isEmpty(product) && !!product.noProduct;
    const hasValidProduct = !isEmpty(product) && !product.noProduct;

    // The account has no published RETTS package: drop any previous session's
    // iframe so we don't show stale content, then fall through to the message.
    if (noProduct) {
        embedKeyRef.current = '';
        urlRef.current = '';
        tokenRef.current = '';
        productRef.current = null;
    }

    // Lazy-mount on the first /triage visit, then keep mounted thereafter
    // (embedKeyRef stays set). Capture a new session identity (url/product/token)
    // only when it genuinely changes — language or account switch — so a routine
    // token refresh (same identity) reuses the captured token without reloading.
    if ((isTriage || embedKeyRef.current) && loggedIn && hasValidProduct) {
        const triageUrl = getTriageUrlForLanguage(i18n.language);
        const candidateKey = `${triageUrl}|${getAccountId()}|${product.id}|${product.version}`;
        if (candidateKey !== embedKeyRef.current) {
            embedKeyRef.current = candidateKey;
            urlRef.current = triageUrl;
            productRef.current = product;
            tokenRef.current = getFromLocal(LOCAL_ACCESS_TOKEN) || '';
        }
    }

    const mountedProduct = productRef.current;

    if (embedKeyRef.current && mountedProduct) {
        const queryParams = {
            apiBaseUrl: getApiBaseUrl() || '',
            apiToken: tokenRef.current,
            essPackageId: mountedProduct.id,
            vpPackageId: mountedProduct.vppackageId,
            rettsVersion: mountedProduct.version,
        };

        return (
            <div className="triage" style={{ display: isTriage ? 'block' : 'none' }}>
                <EmbeddedWebpage
                    key={embedKeyRef.current}
                    src={urlRef.current}
                    title={t('TRIAGE_TITLE')}
                    queryParams={queryParams}
                    fallbackMessage={t('TRIAGE_LOAD_ERROR')}
                    retryLabel={t('TRIAGE_RETRY')}
                />
            </div>
        );
    }

    // No live iframe. Off /triage render nothing; on /triage keep the same
    // guidance the page showed before keep-alive (BAS-13570): a "no available
    // versions" message when the account has no package, otherwise a refresh
    // hint while the product is still loading.
    if (!isTriage || !loggedIn) {
        return null;
    }

    return (
        <div className="noVersionMessage">
            <InfoBox title={t(noProduct ? 'NO_AVAILABLE_VERSIONS' : 'REFRESH_PAGE')} />
        </div>
    );
};

// Placeholder kept in the <Switch> so the /triage URL still matches a route:
// the sidebar active-state keeps working and the URL does not fall through to
// NoSuchPage. The real iframe is the keep-alive instance rendered outside the
// <Switch>.
export const TriagePlaceholder = () => null;

export default withTranslation()(KeepAliveTriage);
